import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Phone, Mail, UserRound } from 'lucide-react';
import { clientsApi } from '../../services/api';
import { ContactFormDialog } from './ContactFormDialog';

function ContactRow({ contact, onEdit, onDelete }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`contact-item-${contact.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-800" data-testid={`contact-item-name-${contact.id}`}>{contact.name}</span>
          {contact.function && <span className="text-xs text-slate-500 italic">{contact.function}</span>}
          {contact.is_primary && <Badge className="bg-blue-100 text-blue-800 text-[10px]">Principal</Badge>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-600">
          {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
          {contact.email && <span className="flex items-center gap-1 break-all"><Mail className="h-3 w-3" />{contact.email}</span>}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(contact)} data-testid={`edit-contact-${contact.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        {!contact.is_primary && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onDelete(contact)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-contact-${contact.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function ContactsManager({ client, onChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const contacts = client.contacts || [];

  const openDialog = (contact = null) => {
    setEditing(contact);
    setDialogOpen(true);
  };

  const handleSave = async (data) => {
    if (editing) {
      const res = await clientsApi.updateContact(client.id, editing.id, data);
      onChange(contacts.map((c) => (c.id === editing.id ? res.data : c)), res.data);
      toast.success('Interlocuteur mis à jour');
    } else {
      const res = await clientsApi.addContact(client.id, data);
      onChange([...contacts, res.data], null);
      toast.success('Interlocuteur ajouté');
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Supprimer l'interlocuteur « ${contact.name} » ?`)) return;
    try {
      await clientsApi.deleteContact(client.id, contact.id);
      onChange(contacts.filter((c) => c.id !== contact.id), null);
      toast.success('Interlocuteur supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="border-t pt-4 space-y-3" data-testid="contacts-manager">
      <div className="flex items-center justify-between">
        <span className="text-base font-semibold flex items-center gap-2">
          <UserRound className="h-4 w-4 text-[#2563EB]" /> Interlocuteurs
          <Badge variant="secondary" data-testid="contacts-count">{contacts.length}</Badge>
        </span>
        <Button type="button" variant="outline" size="sm" onClick={() => openDialog()} data-testid="add-contact-button">
          <Plus className="h-4 w-4 mr-1" /> Ajouter
        </Button>
      </div>
      {contacts.length === 0 ? (
        <p className="text-sm text-slate-500">Aucun interlocuteur enregistré.</p>
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => (
            <ContactRow key={c.id} contact={c} onEdit={openDialog} onDelete={handleDelete} />
          ))}
        </div>
      )}
      <ContactFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        title={editing ? "Modifier l'interlocuteur" : 'Nouvel interlocuteur'}
        onSave={handleSave}
      />
    </div>
  );
}
